/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api, { formatINR } from '@/lib/api';
import { Package, MagnifyingGlass, X, Receipt, ClockCounterClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { TRACK } from '@/constants/testIds';

const STATUSES = [
  { key: 'placed', label: 'Placed' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'packed', label: 'Packed' },
  { key: 'out_for_delivery', label: 'Out for delivery' },
  { key: 'delivered', label: 'Delivered' },
];

const CANCELLABLE = new Set(['placed', 'confirmed', 'packed']);

export default function TrackOrder() {
  const [searchParams, setSearchParams] = useSearchParams();
  const lastOrder = (() => { try { return JSON.parse(localStorage.getItem('zk_last_order') || 'null'); } catch (e) { return null; } })();
  const [orderNo, setOrderNo] = useState(searchParams.get('order') || lastOrder?.order_no || '');
  const [phone, setPhone] = useState(lastOrder?.phone || '');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Cancellation
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReasons, setCancelReasons] = useState([]);
  const [reasonCode, setReasonCode] = useState('');
  const [cancelPhone, setCancelPhone] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);

  // History
  const [showHistory, setShowHistory] = useState(false);
  const [histPhone, setHistPhone] = useState('');
  const [histLastOrder, setHistLastOrder] = useState('');
  const [histList, setHistList] = useState(null);
  const [histBusy, setHistBusy] = useState(false);
  const [histError, setHistError] = useState('');

  const fetchIt = async (no, ph) => {
    if (!no) return;
    setLoading(true);
    setError('');
    try {
      const usePhone = (ph !== undefined ? ph : phone) || '';
      const { data } = await api.get(`/orders/track/${no}`, { params: usePhone ? { phone: usePhone } : {} });
      setOrder(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Order not found');
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initial = searchParams.get('order') || lastOrder?.order_no;
    if (initial) fetchIt(initial, lastOrder?.phone || '');
  }, []);

  useEffect(() => {
    api.get('/orders/cancel-reasons').then((r) => setCancelReasons(r.data)).catch(() => {});
  }, []);

  const submit = (e) => {
    e.preventDefault();
    setSearchParams({ order: orderNo }, { replace: true });
    fetchIt(orderNo.trim());
  };

  const openCancel = () => {
    setReasonCode('');
    setCancelPhone('');
    setShowCancel(true);
  };

  const confirmCancel = async () => {
    if (!reasonCode) { toast.error('Please select a reason'); return; }
    if (!cancelPhone.trim()) { toast.error('Please enter the mobile number on this order'); return; }
    setCancelBusy(true);
    try {
      const { data } = await api.post(`/orders/track/${order.order_no}/cancel`, {
        reason_code: reasonCode,
        phone: cancelPhone.trim(),
      });
      setOrder(data);
      setShowCancel(false);
      toast.success('Order cancelled');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancellation failed');
    } finally {
      setCancelBusy(false);
    }
  };

  const fetchHistory = async (e) => {
    e?.preventDefault();
    if (!histPhone || !histLastOrder) { toast.error('Phone and last order number required'); return; }
    setHistBusy(true);
    setHistError('');
    try {
      const { data } = await api.get('/orders/history', { params: { phone: histPhone.trim(), last_order_no: histLastOrder.trim() } });
      setHistList(data);
    } catch (e) {
      setHistError(e?.response?.data?.detail || 'Could not fetch history');
      setHistList(null);
    } finally {
      setHistBusy(false);
    }
  };

  const statusIdx = order ? STATUSES.findIndex((s) => s.key === order.status) : -1;
  const isRevised = order && order.revised_total != null;
  const displayItems = isRevised ? order.revised_items : order?.items;
  const displaySubtotal = isRevised ? order.revised_subtotal : order?.subtotal;
  const displayDelivery = isRevised ? order.revised_delivery_charge : order?.delivery_charge;
  const displayPlatform = isRevised ? order.revised_platform_fee : order?.platform_fee;
  const displayDiscount = isRevised ? order.revised_discount : order?.discount;
  const displayTotal = isRevised ? order.revised_total : order?.total;

  return (
    <div className="fade-in max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-stone-900">Track your order</h1>
          <p className="text-stone-600 mt-1 text-sm">Enter the order number we sent you (e.g. ZK12345678).</p>
        </div>
        <button
          type="button"
          data-testid={TRACK.historyBtn}
          onClick={() => setShowHistory((s) => !s)}
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border border-stone-300 text-stone-700 text-sm font-medium hover:bg-stone-50"
        >
          <ClockCounterClockwise size={16} weight="duotone"/> Order history
        </button>
      </div>

      {lastOrder?.order_no && !order && !loading && (
        <div data-testid="last-order-banner" className="mt-4 p-3 rounded-2xl bg-olive-50 border border-olive-200 text-sm text-olive-900 flex items-center justify-between gap-3">
          <span>Your last order: <strong>{lastOrder.order_no}</strong></span>
          <button type="button" onClick={() => { setOrderNo(lastOrder.order_no); setSearchParams({ order: lastOrder.order_no }, { replace: true }); fetchIt(lastOrder.order_no); }} className="text-xs font-semibold underline">View</button>
        </div>
      )}

      <form onSubmit={submit} className="mt-6 grid sm:grid-cols-[1fr_180px_auto] gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400"/>
          <input data-testid={TRACK.input} value={orderNo} onChange={(e) => setOrderNo(e.target.value)} placeholder="Order number" className="w-full h-12 pl-11 pr-4 rounded-full bg-white border border-stone-200 focus:border-olive-400 focus:outline-none text-sm"/>
        </div>
        <input
          data-testid="track-phone-input"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Mobile no. (for details)"
          inputMode="tel"
          className="h-12 px-4 rounded-full bg-white border border-stone-200 focus:border-olive-400 focus:outline-none text-sm"
        />
        <button type="submit" data-testid={TRACK.btn} className="h-12 px-6 rounded-full bg-terracotta-600 text-white font-semibold hover:bg-terracotta-700">Track</button>
      </form>
      <p className="mt-2 text-xs text-stone-500">Enter your mobile number to view full delivery details. Without it, only order status and totals are shown.</p>

      {showHistory && (
        <div className="mt-5 bg-white border border-stone-200 rounded-3xl p-5">
          <p className="text-sm text-stone-600 mb-3">For privacy, please verify with your mobile number and one previous order number.</p>
          <form onSubmit={fetchHistory} className="grid sm:grid-cols-3 gap-2">
            <input data-testid={TRACK.historyPhone} value={histPhone} onChange={(e) => setHistPhone(e.target.value)} placeholder="Mobile number" className="h-11 px-4 rounded-full bg-cream-50 border border-stone-200 text-sm focus:border-olive-400 focus:outline-none"/>
            <input data-testid={TRACK.historyLastOrder} value={histLastOrder} onChange={(e) => setHistLastOrder(e.target.value)} placeholder="Last order # (ZK...)" className="h-11 px-4 rounded-full bg-cream-50 border border-stone-200 text-sm focus:border-olive-400 focus:outline-none"/>
            <button type="submit" data-testid={TRACK.historyFetchBtn} disabled={histBusy} className="h-11 px-5 rounded-full bg-olive-700 text-cream-50 text-sm font-semibold hover:bg-olive-800 disabled:bg-stone-300">{histBusy ? 'Fetching…' : 'View history'}</button>
          </form>
          {histError && <p className="mt-3 text-red-600 text-sm">{histError}</p>}
          {histList && (
            <ul data-testid={TRACK.historyList} className="mt-4 divide-y divide-stone-100">
              {histList.length === 0 && <li className="text-sm text-stone-500 py-2">No orders found.</li>}
              {histList.map((h) => (
                <li key={h.id} data-testid={TRACK.historyItem(h.order_no)} className="py-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-stone-900">{h.order_no}</p>
                    <p className="text-xs text-stone-500">{new Date(h.created_at).toLocaleString('en-IN')} · {h.status.replace(/_/g, ' ')}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">{formatINR(h.revised_total != null ? h.revised_total : h.total)}</p>
                    <button type="button" onClick={() => { setOrderNo(h.order_no); setShowHistory(false); setSearchParams({ order: h.order_no }, { replace: true }); fetchIt(h.order_no); }} className="text-xs text-terracotta-600 font-semibold hover:underline">Track</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {loading && <p className="mt-6 text-stone-500">Looking up...</p>}
      {error && <p className="mt-6 text-red-600 text-sm">{error}</p>}

      {order && (
        <div data-testid={TRACK.resultCard} className="mt-8 bg-white border border-stone-200 rounded-3xl p-5 sm:p-7">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-wider text-stone-500">Order</p>
              <p className="font-heading text-xl text-stone-900">{order.order_no}</p>
              {order.customer_name || order.customer_phone ? (
                <p className="text-xs text-stone-500 mt-0.5">{order.customer_name}{order.customer_name && order.customer_phone ? ' · ' : ''}{order.customer_phone}</p>
              ) : (
                <p className="text-xs text-amber-700 mt-0.5" data-testid="track-pii-masked">Enter your mobile above to view delivery details</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wider text-stone-500">Total</p>
              <p className="font-heading text-xl text-stone-900">{formatINR(displayTotal)}</p>
              {isRevised && <p className="text-[11px] text-stone-400 line-through">{formatINR(order.total)}</p>}
            </div>
          </div>

          {isRevised && (
            <div data-testid={TRACK.revisedBanner} className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900 flex items-start gap-2">
              <Receipt size={18} weight="duotone" className="text-amber-700 mt-0.5"/>
              <div>
                <p className="font-medium">Order revised by store</p>
                {order.revised_note && <p className="text-xs mt-0.5">{order.revised_note}</p>}
                <p className="text-xs text-amber-700 mt-0.5">Revised on {new Date(order.revised_at).toLocaleString('en-IN')}</p>
              </div>
            </div>
          )}

          <div className="mt-6">
            {order.status === 'cancelled' ? (
              <div data-testid={TRACK.cancelledBanner} className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">
                <p className="font-medium">This order has been cancelled.</p>
                {order.cancel_reason && <p className="text-xs mt-1">Reason: {order.cancel_reason}</p>}
                {order.cancelled_at && <p className="text-xs text-red-500 mt-0.5">on {new Date(order.cancelled_at).toLocaleString('en-IN')}</p>}
              </div>
            ) : (
              <ol className="flex flex-col sm:flex-row gap-3">
                {STATUSES.map((s, i) => {
                  const reached = i <= statusIdx;
                  return (
                    <li key={s.key} className="flex-1 flex items-center gap-2">
                      <span className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold ${reached ? 'bg-olive-700 text-white' : 'bg-stone-100 text-stone-400'}`}>{i + 1}</span>
                      <span className={`text-sm ${reached ? 'text-stone-900 font-medium' : 'text-stone-400'}`}>{s.label}</span>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>

          {CANCELLABLE.has(order.status) && (
            <div className="mt-5">
              <button
                type="button"
                data-testid={TRACK.cancelBtn}
                onClick={openCancel}
                className="h-10 px-5 rounded-full border border-red-200 text-red-700 text-sm font-medium hover:bg-red-50"
              >
                Cancel order
              </button>
              <p className="text-xs text-stone-500 mt-1.5">You can cancel until your order is out for delivery.</p>
            </div>
          )}

          {order.address ? (
            <div className="mt-6 border-t border-stone-100 pt-5">
              <p className="text-xs uppercase tracking-wider text-stone-500">Delivery to</p>
              <p className="text-sm text-stone-800 mt-1">{order.address?.name}, {order.address?.phone}</p>
              <p className="text-sm text-stone-600">{order.address?.line1}{order.address?.line2 ? `, ${order.address.line2}` : ''}, {order.address?.city}, {order.address?.state} - {order.address?.pincode}</p>
            </div>
          ) : order.pii_masked ? (
            <div className="mt-6 border-t border-stone-100 pt-5">
              <p className="text-xs uppercase tracking-wider text-stone-500">Delivery to</p>
              <p className="text-sm text-stone-500 italic mt-1">Delivery details are hidden for privacy. Enter your mobile number above to view.</p>
            </div>
          ) : null}

          <div className="mt-5 border-t border-stone-100 pt-5 space-y-2">
            {displayItems?.map((it) => (
              <div key={it.product_id} className="flex items-center gap-3 text-sm">
                <img src={it.image_url || '/placeholder.png'} alt={it.name} className="w-10 h-10 rounded-lg object-cover bg-stone-100"/>
                <div className="flex-1 min-w-0"><p className="text-stone-900 line-clamp-1">{it.name}</p><p className="text-xs text-stone-500">x{it.quantity} · {it.unit}</p></div>
                <p className="font-medium">{formatINR(it.price * it.quantity)}</p>
              </div>
            ))}
            <div className="border-t border-stone-100 pt-3 mt-2 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-stone-500">Subtotal</span><span className="font-medium">{formatINR(displaySubtotal)}</span></div>
              <div className="flex justify-between"><span className="text-stone-500">Delivery</span><span className="font-medium">{Number(displayDelivery) === 0 ? 'FREE' : formatINR(displayDelivery)}</span></div>
              {(displayPlatform || 0) > 0 && (
                <div className="flex justify-between"><span className="text-stone-500">Platform fee</span><span className="font-medium">{formatINR(displayPlatform)}</span></div>
              )}
              {Number(displayDiscount || 0) > 0 && (
                <div data-testid={TRACK.discountLine} className="flex justify-between text-olive-700"><span>Discount</span><span className="font-medium">- {formatINR(displayDiscount)}</span></div>
              )}
              <div className="flex justify-between font-heading text-base pt-1"><span>Total</span><span>{formatINR(displayTotal)}</span></div>
            </div>
          </div>
        </div>
      )}

      {!order && !loading && !error && (
        <div className="mt-10 text-center text-stone-400">
          <Package size={48} weight="duotone" className="mx-auto"/>
          <p className="mt-2 text-sm">Enter an order number above to track.</p>
        </div>
      )}

      {showCancel && order && (
        <div className="fixed inset-0 z-50 bg-stone-900/40 flex items-end sm:items-center justify-center p-4">
          <div className="bg-white rounded-3xl border border-stone-200 w-full max-w-md p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl">Cancel order {order.order_no}</h2>
              <button data-testid={TRACK.cancelModalClose} type="button" onClick={() => setShowCancel(false)} className="w-8 h-8 rounded-full hover:bg-stone-100 grid place-items-center"><X size={18}/></button>
            </div>
            <label className="text-xs font-medium text-stone-600">Reason</label>
            <select
              data-testid={TRACK.cancelReasonSelect}
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
              className="w-full h-11 px-3 rounded-lg bg-white border border-stone-200 text-sm mt-1"
            >
              <option value="">Select a reason…</option>
              {cancelReasons.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
            </select>
            <label className="text-xs font-medium text-stone-600 mt-4 block">Verify mobile <span className="text-red-600">*</span></label>
            <input
              value={cancelPhone}
              onChange={(e) => setCancelPhone(e.target.value)}
              placeholder="Phone used at checkout"
              required
              className="w-full h-11 px-3 rounded-lg bg-white border border-stone-200 text-sm mt-1"
            />
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setShowCancel(false)} className="h-10 px-5 rounded-full border border-stone-300 text-stone-700 text-sm">Keep order</button>
              <button
                type="button"
                data-testid={TRACK.cancelConfirmBtn}
                onClick={confirmCancel}
                disabled={cancelBusy || !reasonCode || !cancelPhone.trim()}
                className="h-10 px-5 rounded-full bg-red-600 text-white font-semibold hover:bg-red-700 disabled:bg-stone-300 text-sm"
              >
                {cancelBusy ? 'Cancelling…' : 'Confirm cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
